import { BreadcrumbsData, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { calendarLoco } from 'modules/calendar/constants/calendar-loco'
import {
  CalendarSectionsEnum,
  CalendarSectionsLocoKeys,
} from 'modules/calendar/enums/calendar-sections-enum'
import { updateEvent } from 'modules/calendar/events/api/events-api'
import { EventForm } from 'modules/calendar/events/components/event-form'
import { PreviewModal } from 'modules/calendar/events/components/preview-modal'
import { EventTypeEnum } from 'modules/calendar/events/enums/event-type-enum'
import { useEvent } from 'modules/calendar/events/hooks/use-event'
import {
  EventEditFormInterface,
  EventEditInterface,
  EventFormErrors,
  EventId,
} from 'modules/calendar/events/types/event-interface'
import { getDateOnly } from 'modules/calendar/utils/date-time-formatters'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { Loader } from 'shared/components/loader'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { handle422Error } from 'shared/utils/parse-422-errors'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'

function EventEdit() {
  const { t } = useLocoTranslation()

  const router = useRouter()
  const eventId = Number(router.query.eventId) as unknown as EventId
  const eventType = router.query.eventType as EventTypeEnum

  const [errors, setErrors] = useState<EventFormErrors | null>(null)
  const [isPreviewOpened, setIsPreviewOpened] = useState(false)

  const { eventData, isLoading } = useEvent(eventId)

  const { revalidateModules } = useRevalidateModules()

  const onSubmit: SubmitHandler<EventEditFormInterface> = async values => {
    try {
      setErrors(null)
      const event: EventEditInterface = {
        ...values,
        startDate: getDateOnly(values.startDate),
        endDate: getDateOnly(values.endDate),
      }
      if (!values.useGlobalAvailability && values.localUserAvailability) {
        event.localUserAvailability = {
          dailySettings: values.localUserAvailability.dailySettings.map(slot => ({
            startTime: slot.startTime,
            endTime: slot.endTime,
            date: slot.date,
            dayOfWeek: slot.dayOfWeek,
          })),
        }
        event.localUserAvailability.id = eventData?.localUserAvailability?.id
      } else delete event.localUserAvailability

      await updateEvent(event)
      await revalidateModules([RequestModuleEnum.Calendar, RequestModuleEnum.CalendarBookings])
      toast.success(t('dashboard.calendar.event_update_success'))
      setIsPreviewOpened(true)
    } catch (e) {
      if (e instanceof BadRequest) {
        if (e.errors.common.length !== 0) {
          toast.error(
            <span
              className={'[&>a]:!primary-link'}
              dangerouslySetInnerHTML={{ __html: e.errors.common.join('') }}
            />,
          )
        }
      } else {
        handle422Error(e, setErrors)
      }
    }
  }

  const onPreviewClose = async () => {
    setIsPreviewOpened(false)
    await router.push(CalendarSectionsEnum.events)
  }

  const breadcrumbsData: BreadcrumbsData[] = [
    {
      caption: t(CalendarSectionsLocoKeys.events),
      href: CalendarSectionsEnum.events,
      isStatic: true,
    },
    {
      caption: eventData?.name || '',
      isStatic: true,
    },
  ]

  if (isLoading || !eventData) {
    return (
      <div className="m-auto table">
        <Loader type="dark" />
      </div>
    )
  }

  return (
    <div className="mx-auto flex max-w-[1500px] flex-col gap-5">
      <EventForm
        onSubmit={values => onSubmit(values as EventEditFormInterface)}
        eventData={eventData}
        breadcrumbsData={breadcrumbsData}
        errors={errors}
        eventType={eventType}
      />
      <PreviewModal eventId={eventId} opened={isPreviewOpened} onClose={onPreviewClose} />
    </div>
  )
}

export const getServerSideProps = getBaseServerSideProps(calendarLoco, 'header.top_menu.events')

export default EventEdit
